import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getChannels, getFavorites, getHistory, addFavorite, removeFavorite } from '../services/api';
import { toast } from 'sonner';
import { Search, Heart, Play, ChevronRight, ChevronDown, X, ArrowLeft } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import Hls from 'hls.js';

// Categorize channel by country and subcategory
const categorizeChannel = (name, group) => {
  const nameLower = name.toLowerCase();
  const groupLower = (group || '').toLowerCase();
  
  // US Local stations by network
  const abcStations = ['wabc', 'kabc', 'wls', 'kgo', 'wpvi', 'wtvd', 'wsb', 'ktrk', 'wfaa', 'kmgh'];
  const cbsStations = ['wcbs', 'kcbs', 'wbbm', 'kpix', 'wcco', 'ktvt', 'wfor', 'khou', 'wusa'];
  const nbcStations = ['wnbc', 'knbc', 'wmaq', 'kntv', 'wtvj', 'kxas', 'wcau', 'wrc', 'king'];
  const foxStations = ['wnyw', 'kttv', 'wfld', 'ktvu', 'wttg', 'kdfw', 'waga', 'kriv', 'wjbk'];
  
  if (nameLower.includes('abc') && (nameLower.includes('local') || abcStations.some(s => nameLower.includes(s)) || (nameLower.includes('us') && !nameLower.includes('aust')))) {
    return { country: 'USA', subcategory: 'LOCAL - ABC' };
  }
  if (nameLower.includes('cbs') && (nameLower.includes('local') || cbsStations.some(s => nameLower.includes(s)) || nameLower.includes('us'))) {
    return { country: 'USA', subcategory: 'LOCAL - CBS' };
  }
  if (nameLower.includes('nbc') && (nameLower.includes('local') || nbcStations.some(s => nameLower.includes(s)) || nameLower.includes('us'))) {
    return { country: 'USA', subcategory: 'LOCAL - NBC' };
  }
  if (nameLower.includes('fox') && !nameLower.includes('fox news') && (nameLower.includes('local') || foxStations.some(s => nameLower.includes(s)) || (nameLower.includes('us') && !nameLower.includes('focus')))) {
    return { country: 'USA', subcategory: 'LOCAL - FOX' };
  }
  
  // USA channels
  const usaKeywords = ['usa', 'us ', ' us', 'america', 'cnn', 'espn', 'hbo', 'nfl', 'nba', 'mlb', 'fox news', 'msnbc', 'pluto', 'tubi', 'pbs', 'amc', 'showtime', 'starz', 'bet', 'mtv', 'vh1', 'discovery', 'history channel', 'tlc', 'lifetime', 'hallmark', 'bravo', 'e!', 'syfy', 'usa network'];
  if (usaKeywords.some(k => nameLower.includes(k))) {
    if (groupLower.includes('sport') || nameLower.includes('sport') || nameLower.includes('espn') || nameLower.includes('nfl') || nameLower.includes('nba') || nameLower.includes('mlb')) {
      return { country: 'USA', subcategory: 'SPORTS' };
    }
    if (groupLower.includes('news') || nameLower.includes('news') || nameLower.includes('cnn') || nameLower.includes('msnbc') || nameLower.includes('fox news')) {
      return { country: 'USA', subcategory: 'NEWS' };
    }
    if (groupLower.includes('movie') || nameLower.includes('movie') || nameLower.includes('hbo') || nameLower.includes('showtime') || nameLower.includes('starz') || nameLower.includes('cinemax')) {
      return { country: 'USA', subcategory: 'MOVIES' };
    }
    if (groupLower.includes('kid') || nameLower.includes('kid') || nameLower.includes('nick') || nameLower.includes('disney') || nameLower.includes('cartoon')) {
      return { country: 'USA', subcategory: 'FAMILY & KIDS' };
    }
    if (groupLower.includes('music') || nameLower.includes('mtv') || nameLower.includes('vh1') || nameLower.includes('bet')) {
      return { country: 'USA', subcategory: 'MUSIC' };
    }
    if (groupLower.includes('document') || nameLower.includes('discovery') || nameLower.includes('history') || nameLower.includes('nat geo')) {
      return { country: 'USA', subcategory: 'DOCUMENTARIES' };
    }
    return { country: 'USA', subcategory: 'ENTERTAINMENT' };
  }
  
  // Spanish
  const spanishKeywords = ['latino', 'español', 'espanol', 'spanish', 'telemundo', 'univision', 'azteca', 'televisa', 'mexico', 'caracol', 'rcn', 'telecinco', 'antena 3', 'cuatro', 'la sexta'];
  if (spanishKeywords.some(k => nameLower.includes(k))) {
    if (groupLower.includes('sport')) return { country: 'SPANISH', subcategory: 'SPORTS' };
    if (groupLower.includes('news')) return { country: 'SPANISH', subcategory: 'NEWS' };
    if (groupLower.includes('movie')) return { country: 'SPANISH', subcategory: 'MOVIES' };
    if (groupLower.includes('kid')) return { country: 'SPANISH', subcategory: 'KIDS' };
    if (groupLower.includes('entertainment')) return { country: 'SPANISH', subcategory: 'ENTERTAINMENT' };
    return { country: 'SPANISH', subcategory: 'GENERAL' };
  }
  
  // UK
  const ukKeywords = ['bbc', 'itv', 'sky uk', 'channel 4', 'channel 5', 'british', ' uk', 'uk ', 'london'];
  if (ukKeywords.some(k => nameLower.includes(k))) {
    if (groupLower.includes('sport')) return { country: 'UK', subcategory: 'SPORTS' };
    if (groupLower.includes('news')) return { country: 'UK', subcategory: 'NEWS' };
    if (groupLower.includes('movie')) return { country: 'UK', subcategory: 'MOVIES' };
    if (groupLower.includes('entertainment')) return { country: 'UK', subcategory: 'ENTERTAINMENT' };
    return { country: 'UK', subcategory: 'GENERAL' };
  }
  
  // French
  const frenchKeywords = ['france', 'french', 'français', 'tf1', 'arte', 'm6', 'canal+', 'bfm', 'rmc', 'c8', 'w9'];
  if (frenchKeywords.some(k => nameLower.includes(k))) {
    if (groupLower.includes('sport')) return { country: 'FRENCH', subcategory: 'SPORTS' };
    if (groupLower.includes('news')) return { country: 'FRENCH', subcategory: 'NEWS' };
    if (groupLower.includes('movie')) return { country: 'FRENCH', subcategory: 'MOVIES' };
    if (groupLower.includes('entertainment')) return { country: 'FRENCH', subcategory: 'ENTERTAINMENT' };
    return { country: 'FRENCH', subcategory: 'GENERAL' };
  }
  
  // German
  const germanKeywords = ['german', 'deutsch', 'zdf', 'ard', 'rtl', 'sat.1', 'pro7', 'vox', 'kabel'];
  if (germanKeywords.some(k => nameLower.includes(k))) {
    if (groupLower.includes('sport')) return { country: 'GERMAN', subcategory: 'SPORTS' };
    if (groupLower.includes('news')) return { country: 'GERMAN', subcategory: 'NEWS' };
    return { country: 'GERMAN', subcategory: 'GENERAL' };
  }
  
  // Italian
  const italianKeywords = ['italia', 'italian', 'rai', 'mediaset', 'canale', 'la7', 'sky italia'];
  if (italianKeywords.some(k => nameLower.includes(k))) {
    return { country: 'ITALIAN', subcategory: 'GENERAL' };
  }
  
  // Portuguese/Brazilian
  const portugueseKeywords = ['portugal', 'portuguese', 'brasil', 'brazil', 'globo', 'sic', 'tvi', 'rtp', 'band'];
  if (portugueseKeywords.some(k => nameLower.includes(k))) {
    return { country: 'PORTUGUESE', subcategory: 'GENERAL' };
  }
  
  // Arabic
  const arabicKeywords = ['arabic', 'arab', 'al jazeera', 'mbc', 'rotana', 'dubai', 'saudi', 'qatar', 'egypt'];
  if (arabicKeywords.some(k => nameLower.includes(k))) {
    return { country: 'ARABIC', subcategory: 'GENERAL' };
  }
  
  // Asian
  const asianKeywords = ['chinese', 'china', 'cctv', 'japanese', 'japan', 'nhk', 'korean', 'korea', 'kbs', 'indian', 'india', 'zee', 'star plus', 'philippines', 'vietnam', 'thai'];
  if (asianKeywords.some(k => nameLower.includes(k))) {
    return { country: 'ASIAN', subcategory: 'GENERAL' };
  }
  
  // Default - categorize by content type
  if (groupLower.includes('sport')) return { country: 'INTERNATIONAL', subcategory: 'SPORTS' };
  if (groupLower.includes('news')) return { country: 'INTERNATIONAL', subcategory: 'NEWS' };
  if (groupLower.includes('movie')) return { country: 'INTERNATIONAL', subcategory: 'MOVIES' };
  if (groupLower.includes('kid')) return { country: 'INTERNATIONAL', subcategory: 'KIDS' };
  if (groupLower.includes('music')) return { country: 'INTERNATIONAL', subcategory: 'MUSIC' };
  if (groupLower.includes('religious')) return { country: 'INTERNATIONAL', subcategory: 'RELIGIOUS' };
  if (groupLower.includes('entertainment')) return { country: 'INTERNATIONAL', subcategory: 'ENTERTAINMENT' };
  
  return { country: 'INTERNATIONAL', subcategory: 'GENERAL' };
};

export default function LiveTVPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [channels, setChannels] = useState([]);
  const [allChannels, setAllChannels] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [history, setHistory] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [expandedCountries, setExpandedCountries] = useState(['USA']);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isPlaying, setIsPlaying] = useState(false);
  const [streamError, setStreamError] = useState(null);
  const [categoryData, setCategoryData] = useState({});
  
  const videoRef = useRef(null);
  const hlsRef = useRef(null);

  // Update time
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [favoritesData, historyData, channelsData] = await Promise.all([
          getFavorites(),
          getHistory(),
          getChannels({ limit: 5000 })
        ]);
        
        setFavorites(favoritesData || []);
        setHistory(historyData || []);
        setAllChannels(channelsData || []);
        
        // Build category structure
        const data = {};
        const channelsList = channelsData || [];
        
        for (let i = 0; i < channelsList.length; i++) {
          const ch = channelsList[i];
          const { country, subcategory } = categorizeChannel(ch.name, ch.group);
          
          if (!data[country]) {
            data[country] = { total: 0, subcategories: {} };
          }
          if (!data[country].subcategories[subcategory]) {
            data[country].subcategories[subcategory] = 0;
          }
          data[country].total++;
          data[country].subcategories[subcategory]++;
        }
        
        setCategoryData(data);
        setChannels(channelsData || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filter channels
  useEffect(() => {
    let filtered = allChannels.slice();
    
    if (selectedCountry === 'favorites') {
      filtered = favorites;
    } else if (selectedCountry === 'history') {
      filtered = history.map(h => h.channel).filter(Boolean);
    } else if (selectedCountry && selectedSubcategory) {
      filtered = allChannels.filter(ch => {
        const { country, subcategory } = categorizeChannel(ch.name, ch.group);
        return country === selectedCountry && subcategory === selectedSubcategory;
      });
    } else if (selectedCountry) {
      filtered = allChannels.filter(ch => {
        const { country } = categorizeChannel(ch.name, ch.group);
        return country === selectedCountry;
      });
    }
    
    if (searchQuery) {
      filtered = filtered.filter(ch => 
        ch.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    setChannels(filtered);
  }, [selectedCountry, selectedSubcategory, searchQuery, allChannels, favorites, history]);

  // Play channel via backend HLS proxy (resolves CORS and mixed-content)
  // Falls back to direct browser URL on 421 (proxy blocked by stream server)
  const playChannel = useCallback((channel) => {
    setSelectedChannel(channel);
    setIsPlaying(false);
    setStreamError(null);

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    if (!channel || !channel.url) return;
    const video = videoRef.current;
    if (!video) return;

    const proxyUrl = `${BACKEND_URL}/api/stream/proxy/${channel.id}`;

    const setupHls = (url) => {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
        xhrSetup: (xhr) => {
          if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }
      });
      hlsRef.current = hls;
      hls.loadSource(url);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.play().then(() => setIsPlaying(true)).catch(console.error);
      });
      hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          if (data.response && data.response.code === 421) {
            // Backend proxy blocked — try direct browser URL
            const directUrl = data.response?.text ? JSON.parse(data.response.text)?.direct_url : channel.url;
            if (directUrl) {
              hls.destroy();
              hlsRef.current = null;
              tryDirectPlay(directUrl);
            } else {
              setIsPlaying(false);
              setStreamError('Stream unavailable — server blocks external access.');
            }
          } else {
            setIsPlaying(false);
            setStreamError('Stream unavailable. Try another channel.');
          }
        }
      });
    };

    const tryDirectPlay = (url) => {
      if (Hls.isSupported() && (url.includes('.m3u8') || url.includes('/live/'))) {
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
        });
        hlsRef.current = hls;
        hls.loadSource(url);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          video.play().then(() => setIsPlaying(true)).catch(() => {
            setIsPlaying(false);
            setStreamError('Stream requires HTTPS. Contact your provider to enable SSL.');
          });
        });
        hls.on(Hls.Events.ERROR, (event, data) => {
          if (data.fatal) {
            setIsPlaying(false);
            setStreamError('Stream requires HTTPS. Contact your provider to enable SSL on the panel.');
          }
        });
      } else {
        video.src = url;
        video.play().then(() => setIsPlaying(true)).catch(() => {
          setIsPlaying(false);
          setStreamError('Stream requires HTTPS. Contact your provider to enable SSL.');
        });
      }
    };

    if (Hls.isSupported()) {
      setupHls(proxyUrl);
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = channel.url;
      video.play().then(() => setIsPlaying(true)).catch(() => {
        setIsPlaying(false);
        setStreamError('Stream unavailable on this device.');
      });
    } else {
      setStreamError('Your browser does not support HLS streaming.');
    }
  }, [token, BACKEND_URL]);

  useEffect(() => {
    return () => {
      if (hlsRef.current) hlsRef.current.destroy();
    };
  }, []);

  const handleFavoriteToggle = async (channel, e) => {
    if (e) e.stopPropagation();
    const isFav = favorites.some(f => f.id === channel.id);
    try {
      if (isFav) {
        await removeFavorite(channel.id);
        setFavorites(prev => prev.filter(f => f.id !== channel.id));
        toast.success('Removed from favorites');
      } else {
        await addFavorite(channel.id);
        setFavorites(prev => [...prev, channel]);
        toast.success('Added to favorites');
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  const toggleCountry = (country) => {
    setExpandedCountries(prev => 
      prev.includes(country) ? prev.filter(c => c !== country) : [...prev, country]
    );
  };

  const selectCategory = (country, subcategory = null) => {
    setSelectedCountry(country);
    setSelectedSubcategory(subcategory);
  };

  const generateEPGData = () => {
    const programs = [];
    const baseTime = new Date();
    baseTime.setMinutes(0, 0, 0);
    for (let i = -2; i < 6; i++) {
      const startTime = new Date(baseTime.getTime() + i * 60 * 60 * 1000);
      programs.push({
        title: i === 0 ? (selectedChannel ? selectedChannel.name : 'Current Program') : 'Program ' + (i + 3),
        startTime,
        isCurrent: i === 0
      });
    }
    return programs;
  };

  const epgPrograms = generateEPGData();

  // Country order and flags
  const countryOrder = ['USA', 'SPANISH', 'UK', 'FRENCH', 'GERMAN', 'ITALIAN', 'PORTUGUESE', 'ARABIC', 'ASIAN', 'INTERNATIONAL'];
  const countryFlags = {
    'USA': '🇺🇸',
    'SPANISH': '🇪🇸',
    'UK': '🇬🇧',
    'FRENCH': '🇫🇷',
    'GERMAN': '🇩🇪',
    'ITALIAN': '🇮🇹',
    'PORTUGUESE': '🇧🇷',
    'ARABIC': '🇸🇦',
    'ASIAN': '🌏',
    'INTERNATIONAL': '🌐'
  };

  return (
    <div className="live-tv-page" data-testid="live-tv-page">
      <div className="live-tv-grid-bg">
        <div className="grid-perspective"></div>
      </div>

      <header className="live-tv-header">
        <div className="header-left">
          <button className="header-back-btn" onClick={() => navigate('/')}>
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="xtream-logo">
            <img src="/logo.png" alt="FTA Xtream" style={{height:'36px', width:'auto', objectFit:'contain'}} />
          </div>
          <button className="header-close-btn" onClick={() => navigate('/')}>
            <X className="w-5 h-5" />
          </button>
        </div>
      </header>

      <div className="live-tv-content">
        <div className="categories-sidebar" data-testid="categories-sidebar">
          <div className="sidebar-search">
            <Search className="w-4 h-4" />
            <input 
              type="text" 
              placeholder="Search in categories"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <ScrollArea className="categories-list">
            {/* All Channels */}
            <button
              className={'category-item' + (!selectedCountry ? ' active' : '')}
              onClick={() => selectCategory(null)}
            >
              <span className="category-name">ALL CHANNELS</span>
              <span className="category-count">{allChannels.length}</span>
            </button>

            {/* Favorites */}
            <button
              className={'category-item' + (selectedCountry === 'favorites' ? ' active' : '')}
              onClick={() => selectCategory('favorites')}
            >
              <span className="category-name">⭐ FAVOURITES</span>
              <span className="category-count">{favorites.length}</span>
            </button>

            {/* History */}
            <button
              className={'category-item' + (selectedCountry === 'history' ? ' active' : '')}
              onClick={() => selectCategory('history')}
            >
              <span className="category-name">🕐 HISTORY</span>
              <span className="category-count">{history.length}</span>
            </button>

            <div className="category-divider"></div>

            {/* Countries with subcategories */}
            {countryOrder.map(country => {
              const data = categoryData[country];
              if (!data || data.total === 0) return null;
              
              const isExpanded = expandedCountries.includes(country);
              const subcats = Object.entries(data.subcategories).sort((a, b) => b[1] - a[1]);
              
              return (
                <div key={country} className="country-group">
                  <button
                    className="country-header"
                    onClick={() => toggleCountry(country)}
                  >
                    {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    <span className="country-flag">{countryFlags[country]}</span>
                    <span className="country-name">{country}</span>
                    <span className="country-count">{data.total}</span>
                  </button>
                  
                  {isExpanded && (
                    <div className="subcategory-list">
                      {subcats.map(([subcat, count]) => {
                        const isActive = selectedCountry === country && selectedSubcategory === subcat;
                        return (
                          <button
                            key={subcat}
                            className={'category-item subcategory' + (isActive ? ' active' : '')}
                            onClick={() => selectCategory(country, subcat)}
                          >
                            <span className="category-name">{country} | {subcat}</span>
                            <span className="category-count">{count}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </ScrollArea>
        </div>

        <div className="channels-list-container" data-testid="channels-list">
          <div className="channels-header">
            <span className="channels-count">{channels.length} channels</span>
          </div>
          <ScrollArea className="channels-scroll">
            {loading ? (
              <div className="channels-loading">
                {[1,2,3,4,5].map(i => <div key={i} className="channel-item-skeleton"></div>)}
              </div>
            ) : channels.length === 0 ? (
              <div className="channels-empty"><p>No channels found</p></div>
            ) : (
              channels.slice(0, 200).map(channel => {
                const isActive = selectedChannel && selectedChannel.id === channel.id;
                const isFav = favorites.some(f => f.id === channel.id);
                return (
                  <div
                    key={channel.id}
                    className={'channel-item' + (isActive ? ' active' : '')}
                    onClick={() => playChannel(channel)}
                  >
                    <div className="channel-logo">
                      {channel.logo ? (
                        <img src={channel.logo} alt="" onError={(e) => { e.target.style.display = 'none'; }} />
                      ) : (
                        <div className="channel-logo-placeholder">{channel.name.charAt(0)}</div>
                      )}
                    </div>
                    <div className="channel-info">
                      <span className="channel-name">{channel.name}</span>
                      <span className="channel-program">{channel.group || 'Live TV'}</span>
                    </div>
                    <button 
                      className={'channel-fav-btn' + (isFav ? ' active' : '')}
                      onClick={(e) => handleFavoriteToggle(channel, e)}
                    >
                      <Heart className="w-4 h-4" />
                    </button>
                  </div>
                );
              })
            )}
          </ScrollArea>
        </div>

        <div className="video-player-container" data-testid="video-player">
          <div className="video-wrapper">
            <video ref={videoRef} className="video-element" controls={isPlaying} playsInline />
            {!isPlaying && (
              <div className="video-overlay">
                <div className="video-overlay-content">
                  {streamError ? (
                    <>
                      <Play className="w-12 h-12 text-red-400" />
                      <span className="text-red-400 text-sm text-center px-4">{streamError}</span>
                    </>
                  ) : (
                    <>
                      <Play className="w-16 h-16" />
                      <span>Select a channel to play</span>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <div className="epg-container" data-testid="epg-guide">
            <div className="epg-header">
              <span className="epg-title">CHANNEL GUIDE</span>
              <span className="epg-channel">{selectedChannel ? selectedChannel.name : 'No channel selected'}</span>
            </div>
            <div className="epg-timeline">
              {epgPrograms.map((program, idx) => (
                <div key={idx} className={'epg-program' + (program.isCurrent ? ' current' : '')} style={{ width: (100 / epgPrograms.length) + '%' }}>
                  <div className="epg-time">{program.startTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}</div>
                  <div className="epg-program-title">{program.title}</div>
                  {program.isCurrent && (
                    <div className="epg-progress">
                      <div className="epg-progress-bar" style={{ width: ((currentTime.getMinutes() / 60) * 100) + '%' }} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
